/**
 * Balance constants. Starting values for tuning (GDD §2, §3); every number
 * that affects play lives here or in other content files, never in sim code.
 */
export const BALANCE = {
  /** Simulation tick length (10 ticks per second). */
  tickMs: 100,
  /** Economic cycle: markets, upkeep, growth (30 s). */
  cycleTicks: 300,
  /** Network pauses: 3 pauses of 30 s per player (GDD §2). */
  pausesPerPlayer: 3,
  pauseTicks: 300,
  /** Hard session limit: 75 minutes. */
  sessionLimitTicks: 75 * 60 * 10,
  /** Map radius in hexes by player count (GDD §3). */
  mapRadiusByPlayers: { 2: 14, 3: 16, 4: 18 } as const,
  /** Number of orbits generated around the star. */
  orbitCount: 7,
  /** Minimum hex distance between any two capitals (fairness check). */
  minCapitalDistance: 8,
  /** Guaranteed level-1 dust clouds within `startPocketRadius` of each capital. */
  startPocketClouds: 2,
  startPocketRadius: 4,
  /** Map generation retries before giving up on fairness. */
  mapGenAttempts: 100,
  /**
   * Lagrange points (GDD §3): the gap between the capital closest to its
   * nearest L4/L5 point and the one farthest from it, in hexes, at most.
   */
  lagrangeFairnessSpread: 6,
} as const;

export type PlayerCount = keyof typeof BALANCE.mapRadiusByPlayers;

/**
 * How rich the system is (#98, the author's request of 21.10.2026): the
 * same set of bodies and dust clouds within `reach` of every capital, so a
 * seed never decides who starts better. Asteroids sit on the belt, gas
 * giants on the giants' orbits, rocky planets on the others; clouds by
 * level. `middle` clouds lie beyond every capital's reach, to fight over.
 */
export const RICHNESS_IDS = ['scarce', 'balanced', 'rich'] as const;
export type RichnessId = (typeof RICHNESS_IDS)[number];

export interface RichnessDef {
  /** Hexes from a capital that count as its own surroundings. */
  readonly reach: number;
  readonly perCapital: {
    readonly asteroids: number;
    readonly planets: number;
    readonly gasGiants: number;
    readonly clouds: Readonly<Record<1 | 2 | 3, number>>;
  };
  readonly middle: { readonly clouds: Readonly<Record<1 | 2 | 3, number>> };
}

export const RICHNESS: Readonly<Record<RichnessId, RichnessDef>> = {
  scarce: {
    reach: 10,
    perCapital: { asteroids: 1, planets: 1, gasGiants: 1, clouds: { 1: 2, 2: 1, 3: 0 } },
    middle: { clouds: { 1: 1, 2: 1, 3: 1 } },
  },
  balanced: {
    reach: 10,
    perCapital: { asteroids: 2, planets: 1, gasGiants: 1, clouds: { 1: 3, 2: 1, 3: 0 } },
    middle: { clouds: { 1: 1, 2: 1, 3: 1 } },
  },
  rich: {
    reach: 10,
    perCapital: { asteroids: 3, planets: 2, gasGiants: 1, clouds: { 1: 4, 2: 2, 3: 1 } },
    middle: { clouds: { 1: 2, 2: 1, 3: 1 } },
  },
};

/**
 * Bodies keep their distance (#98): no two planets or gas giants side by
 * side; an asteroid field may lie right by a planet, but not on another field.
 */
export const MAP_SPACING = {
  /** Between planets, gas giants and capitals, in hexes. */
  bodies: 3,
  /** Between two asteroid fields. */
  asteroids: 2,
  /** Between an asteroid field and anything else: never the same hex. */
  asteroidToBody: 1,
} as const;
